import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useT } from '../../i18n/LangProvider';

type Opt = { id: string; labelEn: string; labelFr: string };
type Meal = { id: string; nameEn: string; nameFr: string; options: Opt[] };
type Config = { departments: Opt[]; dietary: Opt[]; accessibility: Opt[]; meals: Meal[] };
type Submission = { email: string; data: Record<string, unknown>; submittedAt: string };

async function handle<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let msg = `Error ${res.status}`;
    try { const j = await res.json(); msg = j.error || msg; } catch { /* noop */ }
    throw new Error(msg);
  }
  return res.json();
}

const toggle = (set: Set<string>, setter: (s: Set<string>) => void, id: string) => {
  const next = new Set(set);
  next.has(id) ? next.delete(id) : next.add(id);
  setter(next);
};

function Check({ opts, sel, setter, other, setOther, lbl, otherLabel }: {
  opts: Opt[]; sel: Set<string>; setter: (s: Set<string>) => void;
  other: string; setOther: (v: string) => void;
  lbl: (o: Opt) => string; otherLabel: string;
}) {
  return (
    <div className="space-y-2">
      {opts.map((o) => {
        const on = sel.has(o.id);
        return (
          <button key={o.id} type="button" onClick={() => toggle(sel, setter, o.id)}
            className={`w-full text-left flex items-center gap-3 p-3 rounded-xl border transition-all ${on ? 'bg-brand-red/[0.08] border-brand-red' : 'bg-bg-base border-white/[0.08] hover:border-white/25'}`}>
            <span className={`flex-shrink-0 w-5 h-5 rounded flex items-center justify-center text-xs ${on ? 'bg-brand-red text-white' : 'border-2 border-ink-faint'}`}>{on && '✓'}</span>
            <span className="text-ink text-sm">{lbl(o)}</span>
          </button>
        );
      })}
      <input value={other} onChange={(e) => setOther(e.target.value)} placeholder={otherLabel} className="w-full bg-bg-base border border-white/[0.08] rounded-xl px-3 py-2.5 text-sm text-ink outline-none focus:border-brand-red mt-1" />
    </div>
  );
}

export default function RegisterPage() {
  const { t, lang } = useT();
  const [cfg, setCfg] = useState<Config>({ departments: [], dietary: [], accessibility: [], meals: [] });
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [existing, setExisting] = useState<Submission | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  // form state
  const [fullName, setFullName] = useState('');
  const [department, setDepartment] = useState('');
  const [manager, setManager] = useState('');
  const [dietary, setDietary] = useState<Set<string>>(new Set());
  const [dietaryOther, setDietaryOther] = useState('');
  const [access, setAccess] = useState<Set<string>>(new Set());
  const [accessOther, setAccessOther] = useState('');
  const [mealChoices, setMealChoices] = useState<Record<string, string>>({});

  const lbl = (o: Opt) => (lang === 'fr' ? o.labelFr : o.labelEn) || o.labelEn;
  const mealName = (m: Meal) => (lang === 'fr' ? m.nameFr : m.nameEn) || m.nameEn;

  useEffect(() => {
    (async () => {
      try {
        const [c, my] = await Promise.all([
          handle<Config>(await fetch('/api/reg-config')),
          handle<{ submission: Submission | null; allowed: boolean }>(await fetch('/api/reg-my'))
        ]);
        setCfg(c);
        setExisting(my.submission);
        setAllowed(my.allowed);
      } catch (e) {
        const msg = (e as Error).message;
        if (msg.includes('401')) { window.location.href = '/.auth/login/aad?post_login_redirect_uri=/register'; return; }
        setError(msg);
      } finally { setLoading(false); }
    })();
  }, []);

  const submit = async () => {
    if (!fullName.trim() || !department || !manager.trim()) { setError(t('register.required')); return; }
    for (const m of cfg.meals) { if (!mealChoices[m.id]) { setError(t('register.required')); return; } }
    setSubmitting(true); setError(null);
    const data = {
      fullName: fullName.trim(),
      department,
      manager: manager.trim(),
      dietary: [...dietary],
      dietaryOther: dietaryOther.trim(),
      accessibility: [...access],
      accessibilityOther: accessOther.trim(),
      meals: mealChoices
    };
    try {
      const r = await handle<{ submission: Submission }>(await fetch('/api/reg-submit', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ data })
      }));
      setExisting(r.submission); setDone(true);
    } catch (e) {
      const msg = (e as Error).message;
      setError(msg === 'NOT_ALLOWED' ? t('register.notAllowed') : msg);
    } finally { setSubmitting(false); }
  };

  const FIELD = 'w-full bg-bg-base border border-white/[0.12] rounded-xl px-3 py-2.5 text-sm text-ink outline-none focus:border-brand-red';
  const LABEL = 'text-xs text-ink-muted uppercase tracking-wider mb-2 block';

  return (
    <div className="max-w-2xl mx-auto px-6 md:px-10 pt-36 pb-32">
      <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="text-xs tracking-[0.25em] uppercase text-brand-red mb-5 font-mono">{t('register.kicker')}</motion.p>
      <motion.h1 initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }} className="font-display text-5xl md:text-7xl tracking-tight text-ink">{t('register.title')}</motion.h1>
      <p className="text-ink-muted mt-5 max-w-md text-lg">{t('register.lead')}</p>

      {loading && <p className="text-ink-muted mt-10">…</p>}

      {!loading && allowed === false && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-10 card p-8">
          <h2 className="font-display text-2xl text-ink mb-2">{t('register.notAllowed')}</h2>
          <p className="text-ink-muted">{t('register.notAllowedNote')}</p>
        </motion.div>
      )}

      {!loading && allowed && existing && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-10 card p-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="w-8 h-8 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center text-emerald-400 text-sm">✓</span>
            <h2 className="font-display text-2xl text-ink">{t('register.submitted')}</h2>
          </div>
          <p className="text-xs text-ink-faint">{t('register.submittedNote')}</p>
        </motion.div>
      )}

      {!loading && allowed && !existing && (
        <div className="mt-10 space-y-6">
          <div><span className={LABEL}>{t('register.fullName')} *</span><input className={FIELD} value={fullName} onChange={(e) => setFullName(e.target.value)} /></div>
          <div><span className={LABEL}>{t('register.department')} *</span>
            <select className={FIELD} value={department} onChange={(e) => setDepartment(e.target.value)}>
              <option value="">{t('register.choose')}</option>
              {cfg.departments.map((o) => <option key={o.id} value={o.id}>{lbl(o)}</option>)}
            </select>
          </div>
          <div><span className={LABEL}>{t('register.manager')} *</span><input className={FIELD} value={manager} onChange={(e) => setManager(e.target.value)} /></div>
          <div><span className={LABEL}>{t('register.dietary')}</span><p className="text-xs text-ink-faint mb-2">{t('register.checkNote')}</p><Check opts={cfg.dietary} sel={dietary} setter={setDietary} other={dietaryOther} setOther={setDietaryOther} lbl={lbl} otherLabel={t('register.other')} /></div>
          <div><span className={LABEL}>{t('register.accessibility')}</span><p className="text-xs text-ink-faint mb-2">{t('register.checkNote')}</p><Check opts={cfg.accessibility} sel={access} setter={setAccess} other={accessOther} setOther={setAccessOther} lbl={lbl} otherLabel={t('register.other')} /></div>
          {cfg.meals.map((m) => (
            <div key={m.id}><span className={LABEL}>{mealName(m)} *</span>
              <div className="space-y-2">
                {m.options.map((o) => {
                  const on = mealChoices[m.id] === o.id;
                  return (
                    <button key={o.id} type="button" onClick={() => setMealChoices({ ...mealChoices, [m.id]: o.id })}
                      className={`w-full text-left flex items-center gap-3 p-3 rounded-xl border transition-all ${on ? 'bg-brand-red/[0.08] border-brand-red' : 'bg-bg-base border-white/[0.08] hover:border-white/25'}`}>
                      <span className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs ${on ? 'bg-brand-red text-white' : 'border-2 border-ink-faint'}`}>{on && '✓'}</span>
                      <span className="text-ink text-sm">{lbl(o)}</span>
                    </button>
                  );
                })}
                {m.options.length === 0 && <p className="text-ink-faint text-sm">—</p>}
              </div>
            </div>
          ))}

          <button onClick={submit} disabled={submitting} className="inline-flex items-center gap-2 bg-brand-red text-white text-sm rounded-md px-6 py-3 disabled:opacity-40 hover:bg-brand-red-soft transition-colors">
            {submitting ? t('register.submitting') : t('register.submit')}
          </button>
        </div>
      )}

      {error && <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-5 p-4 rounded-xl bg-red-950/50 border border-red-700/50 text-red-300 text-sm">{error}</motion.p>}
      {done && <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-5 p-4 rounded-xl bg-emerald-950/50 border border-emerald-700/50 text-emerald-300 text-sm">{t('register.success')}</motion.p>}
    </div>
  );
}
